/**
 * Departure countdown timers
 * Interim cutoff: 12:00 AM America/New_York on the departure date.
 * Trips leaving tomorrow stay bookable through the rest of today.
 * On expire: update booking UI, then hide collection cards (PDP stays with expired messaging).
 */
(function () {
  if (window.__departureCountdownLoaded) return;
  window.__departureCountdownLoaded = true;

  const TIMEZONE = 'America/New_York';
  const SELECTOR = '[data-departure-countdown]';
  const DEFAULT_CARD_HIDE_DELAY_MS = 2500;

  /**
   * Convert a wall-clock date/time in a named timezone to a UTC timestamp (ms).
   */
  function zonedDateTimeToUtcMs(year, month, day, hour, minute, second, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    let utcMs = Date.UTC(year, month - 1, day, hour, minute, second);

    for (let i = 0; i < 2; i++) {
      const parts = formatter.formatToParts(new Date(utcMs));
      const values = {};

      for (const part of parts) {
        if (part.type !== 'literal') {
          values[part.type] = parseInt(part.value, 10);
        }
      }

      if (values.hour === 24) {
        values.hour = 0;
      }

      const asUtcMs = Date.UTC(
        values.year,
        values.month - 1,
        values.day,
        values.hour,
        values.minute,
        values.second
      );
      const desiredAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);
      utcMs += desiredAsUtcMs - asUtcMs;
    }

    return utcMs;
  }

  function getCountdownTargetMs(departureDateIso, timeZone) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(departureDateIso);
    if (!match) return null;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    return zonedDateTimeToUtcMs(year, month, day, 0, 0, 0, timeZone || TIMEZONE);
  }

  function pad(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  function splitRemaining(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds, totalMs: ms };
  }

  function getContextRoot(countdownEl) {
    if (!countdownEl || !countdownEl.closest) return null;
    return (
      countdownEl.closest('[data-departure-product]') ||
      countdownEl.closest('.card-wrapper') ||
      countdownEl.closest('.main-product-template') ||
      countdownEl
    );
  }

  function getCollectionItem(countdownEl) {
    if (!countdownEl || !countdownEl.closest) return null;
    return countdownEl.closest('.grid__item, .swiper-slide, [id^="Slide-"]');
  }

  function setCtaExpired(root) {
    if (!root) return;

    root.querySelectorAll('[data-departure-cta]').forEach(function (cta) {
      cta.textContent = 'Booking expired';
      cta.classList.add('departure-cta--expired');
      cta.setAttribute('aria-disabled', 'true');

      if (cta.tagName === 'A') {
        cta.setAttribute('tabindex', '-1');
        cta.addEventListener(
          'click',
          function (event) {
            event.preventDefault();
          },
          { once: false }
        );
      }

      if (cta.tagName === 'BUTTON') {
        cta.disabled = true;
        cta.setAttribute('aria-disabled', 'true');
      }
    });
  }

  function hidePrices(root) {
    if (!root) return;
    root.querySelectorAll('[data-departure-price]').forEach(function (priceEl) {
      priceEl.hidden = true;
      priceEl.setAttribute('aria-hidden', 'true');
    });
  }

  function showExpiredBanner(root) {
    if (!root) return;
    root.querySelectorAll('[data-departure-expired-banner]').forEach(function (banner) {
      banner.hidden = false;
    });
  }

  function disablePdpBooking(root) {
    if (!root) return;

    root.querySelectorAll('[data-departure-hide-when-expired]').forEach(function (el) {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
    });

    root.querySelectorAll('product-form form, form.shopify-product-form').forEach(function (form) {
      form.setAttribute('data-departure-booking-closed', 'true');
      form.addEventListener(
        'submit',
        function (event) {
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    });

    root.querySelectorAll('button[name="add"], button[type="submit"], #depo-add-to-cart, #dummy-book-now').forEach(
      function (button) {
        try {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('departure-cta--expired');
          const label = button.querySelector('span');
          if (label) label.textContent = 'Booking expired';
          else button.textContent = 'Booking expired';
        } catch (e) {
          // App-injected buttons may be detached if Flow archives mid-session.
        }
      }
    );
  }

  function applyExpiredUi(countdownEl) {
    const root = getContextRoot(countdownEl);
    if (!root) return;

    const context = countdownEl.dataset.departureContext || 'card';

    root.classList.add('departure-booking-expired');
    setCtaExpired(root);
    hidePrices(root);

    if (context === 'pdp') {
      showExpiredBanner(root);
      disablePdpBooking(root);
    }
  }

  function hideCollectionProduct(countdownEl) {
    const item = getCollectionItem(countdownEl);
    if (!item || item.dataset.departureHidden === 'true') return;

    item.dataset.departureHidden = 'true';
    item.classList.add('departure-product--hiding');

    window.setTimeout(function () {
      try {
        if (!item.isConnected) return;
        item.classList.add('departure-product--hidden');
        item.setAttribute('hidden', '');
        item.style.display = 'none';
      } catch (e) {
        // Element may already be gone if Flow archived / AJAX refreshed the grid.
      }
    }, 400);
  }

  function scheduleCollectionHide(countdownEl) {
    if ((countdownEl.dataset.departureContext || 'card') !== 'card') return;
    if (countdownEl.dataset.departureHideScheduled === 'true') return;

    countdownEl.dataset.departureHideScheduled = 'true';
    const delay = parseInt(countdownEl.dataset.expireHideDelay || DEFAULT_CARD_HIDE_DELAY_MS, 10);

    window.setTimeout(
      function () {
        try {
          hideCollectionProduct(countdownEl);
        } catch (e) {
          // Safe no-op if the card was removed by archive/sync.
        }
      },
      Number.isFinite(delay) ? delay : DEFAULT_CARD_HIDE_DELAY_MS
    );
  }

  function handleExpired(countdownEl) {
    if (countdownEl.dataset.departureExpired === 'true') return false;
    countdownEl.dataset.departureExpired = 'true';
    countdownEl.hidden = true;

    try {
      applyExpiredUi(countdownEl);
    } catch (e) {
      // Keep page usable even if related nodes were removed mid-update.
    }

    try {
      scheduleCollectionHide(countdownEl);
    } catch (e) {
      // Ignore hide failures (e.g. product already archived out of the DOM).
    }

    return false;
  }

  function updateTimer(el, nowMs) {
    const departureDate = el.dataset.departureDate;
    const timeZone = el.dataset.timezone || TIMEZONE;
    const targetMs = getCountdownTargetMs(departureDate, timeZone);

    if (targetMs == null) {
      el.hidden = true;
      return false;
    }

    const remaining = splitRemaining(targetMs - nowMs);

    if (remaining.totalMs <= 0) {
      return handleExpired(el);
    }

    const daysEl = el.querySelector('[data-days]');
    const hoursEl = el.querySelector('[data-hours]');
    const minutesEl = el.querySelector('[data-minutes]');
    const secondsEl = el.querySelector('[data-seconds]');

    if (daysEl) daysEl.textContent = pad(remaining.days);
    if (hoursEl) hoursEl.textContent = pad(remaining.hours);
    if (minutesEl) minutesEl.textContent = pad(remaining.minutes);
    if (secondsEl) secondsEl.textContent = pad(remaining.seconds);

    el.hidden = false;
    return true;
  }

  function initDepartureCountdowns(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const timers = scope.querySelectorAll(SELECTOR);
    if (!timers.length) return;

    const active = [];

    timers.forEach(function (el) {
      const stillActive = updateTimer(el, Date.now());
      el.dataset.countdownInitialized = 'true';
      if (stillActive) active.push(el);
    });

    if (!active.length || window.__departureCountdownInterval) return;

    window.__departureCountdownInterval = window.setInterval(function () {
      const now = Date.now();
      let anyActive = false;

      document.querySelectorAll(SELECTOR).forEach(function (el) {
        if (updateTimer(el, now)) anyActive = true;
      });

      if (!anyActive) {
        window.clearInterval(window.__departureCountdownInterval);
        window.__departureCountdownInterval = null;
      }
    }, 1000);
  }

  window.initDepartureCountdowns = initDepartureCountdowns;

  function startObserver() {
    if (window.__departureCountdownObserver || typeof MutationObserver === 'undefined') return;

    window.__departureCountdownObserver = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches(SELECTOR)) || (node.querySelector && node.querySelector(SELECTOR))) {
            initDepartureCountdowns(document);
            return;
          }
        }
      }
    });

    window.__departureCountdownObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initDepartureCountdowns(document);
      startObserver();
    });
  } else {
    initDepartureCountdowns(document);
    startObserver();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initDepartureCountdowns(event.target);
  });
})();
